/**
 * @fileoverview Confirms a reviewer's approval of a pending prospect submission.
 * @description Says where the prospect goes once approved — to its proposed owner, still in Sales
 * Pipeline's Backlog until a target is applied — then approves its review request (POST
 * /approvals/{id}/approve, with the request's current version; see useDecideApprovalRequest).
 * The parent mounts it only while a request is being approved, so every opening starts with a
 * fresh mutation. Close is held while the request is in flight, and the confirmation runs from the
 * mutation itself (so it survives an unmount). A failure stays visible in the dialog.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import { useApprovalRequest } from '@/features/sales/hooks/useApprovalRequest'
import { useDecideApprovalRequest } from '@/features/sales/hooks/useDecideApprovalRequest'
import { useRefreshSalesQueries } from '@/features/sales/hooks/useRefreshSalesQueries'
import { requestErrorMessage } from '@/features/sales/lib/request-error.utils'
import { isStaleVersionError } from '@/features/sales/lib/stale-version.utils'
import Modal from '@/shared/components/molecules/Modal'
import { useGuardedClose } from '@/shared/hooks/useGuardedClose'

import DialogActions from './DialogActions'
import StaleVersionNotice from './StaleVersionNotice'

import type { ApprovalListItem } from '@/features/sales/types/approval.types'

interface ApproveProspectDialogProps {
  request: ApprovalListItem
  onClose: () => void
  onApproved: (request: ApprovalListItem) => void
}

/**
 * @component
 * @param {ApproveProspectDialogProps} props - The request and the handlers.
 * @param {ApprovalListItem} props.request - The pending review request.
 * @param {() => void} props.onClose - Called when the dialog is dismissed.
 * @param {(request: ApprovalListItem) => void} props.onApproved - Called once it's approved.
 */
function ApproveProspectDialog({ request, onClose, onApproved }: ApproveProspectDialogProps) {
  const { t } = useTranslation()
  const approval = useApprovalRequest(request.approvalRequestId)
  const decide = useDecideApprovalRequest({
    /**
     * @description Reports the approval to the parent, even if this dialog has unmounted.
     */
    onSuccess: () => onApproved(request),
  })
  const close = useGuardedClose(onClose, decide.isPending)
  const refreshSales = useRefreshSalesQueries()
  const loaded = approval.data
  const isStale = decide.isError && isStaleVersionError(decide.error)
  const proposedOwner = loaded ? loaded.proposedOwner : request.proposedOwner
  const isReady = approval.isSuccess && !approval.isFetching && !isStale

  /**
   * @description Clears a stale refusal and reloads the request's latest state for review.
   */
  const onRefresh = () => {
    decide.reset()
    refreshSales()
  }

  /**
   * @description Approves the request at the version shown; the mutation reports it to the parent.
   */
  const approve = () => {
    if (!loaded) {
      return
    }
    decide.mutate({
      approvalRequestId: request.approvalRequestId,
      approvalVersion: loaded.approvalVersion,
      decision: 'approve',
      reviewNote: null,
    })
  }

  return (
    <Modal
      open
      onClose={close}
      data-testid="approve-prospect-dialog"
      title={t('sales.pipeline.approve.title')}
      subtitle={request.bankName}
    >
      <div className="flex flex-col gap-4">
        <p className="text-[13.5px] text-ink" data-testid="approve-prospect-message">
          {proposedOwner
            ? t('sales.pipeline.approve.toAssignee', { name: proposedOwner.displayName })
            : t('sales.pipeline.approve.toUnassigned')}
        </p>
        {approval.isError && (
          <p
            role="alert"
            className="text-[13px] text-danger"
            data-testid="approve-prospect-load-error"
          >
            {requestErrorMessage(approval.error, t)}
          </p>
        )}
        {isStale && <StaleVersionNotice onRefresh={onRefresh} isRefreshing={approval.isFetching} />}
        {decide.isError && !isStale && (
          <p role="alert" className="text-[13px] text-danger" data-testid="approve-prospect-error">
            {requestErrorMessage(decide.error, t)}
          </p>
        )}
        <DialogActions
          testIdPrefix="approve-prospect"
          cancelLabel={t('sales.pipeline.approve.cancel')}
          confirmLabel={t('sales.pipeline.approve.confirm')}
          onCancel={close}
          onConfirm={approve}
          isLoading={decide.isPending}
          disabled={!isReady}
        />
      </div>
    </Modal>
  )
}

export default ApproveProspectDialog
